import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Factors, type Product } from '@accounts/domain';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Button, Callout, Checklist, EmptyState, Field, Form, ListRow } from '@beyond-js/ui/react';
import { Deletion, Go, Outage, Panel, Problem, Retry, useRead } from '@accounts/ui';
import { Refusal } from '@accounts/transport';
import { PendingInvitations } from './home/invitations';

/**
 * The account home (ACC-18): who the person is, what waits for them, how their account is protected,
 * their organizations, the products this installation runs with what each is, and their recent
 * security activity.
 *
 * A person without a name is asked for one here first, because their teammates otherwise read
 * "Unnamed" (ACC-09). A deletion in its grace is said first, with Keep my account. Opening a product
 * goes to where people open it (`entry`), and only products this installation runs are listed (ARR-16).
 * Where a person works is chosen in each product's organization menu and remembered for the next
 * sign-in (ARR-07); this page no longer has a "Work here".
 *
 * Until the account is set up, the home ends with the family's checklist (DQ-17): a name, a second
 * factor and an organization, each with its one action, done steps folded. Places with nothing yet say
 * so with the family's empty state and their first action.
 *
 * Each of its reads is its own: the products, the ways to sign in, the second factors, the recent
 * security activity and the invitations. When Accounts does not answer them the page says so once
 * (`Outage`, D163), each place says it is not available, and the checklist waits until the factors are
 * known rather than leaving their step out.
 *
 * Nothing appears above what is drawn (D185): the regions are drawn at once and wait in place, and the
 * two parts known only once a read answers come after them, the invitations waiting and then the
 * checklist, which also waits for the invitations' first read, so neither arrival moves what is shown.
 */
export function Overview() {
	const { text, date, identity, accounts, client, refresh, language } = usePresence();
	const [products, setProducts] = useState<Product[]>();
	// How many ways into the account and how many second factors the person has
	const [ways, setWays] = useState<number>();
	const [factors, setFactors] = useState<number>();
	const [recent, setRecent] = useState<{ seq: number; at: string; action: string }[]>();
	const [name, setName] = useState('');
	const [failure, setFailure] = useState<unknown>();
	const [busy, setBusy] = useState(false);
	// Whether the invitations' first read has answered, after which nothing comes above the checklist
	const [asked, setAsked] = useState(false);
	const ignored = Boolean((useLocation().state as { ignored?: boolean } | null)?.ignored);

	// The page's reads, each answered on its own and each read again on its own while Accounts does not answer it
	const listed = useRead(async () => setProducts((await accounts.providers()).products.filter(product => product.id !== 'accounts' && product.offered !== false)));
	const linked = useRead(async () => setWays((await accounts.account()).identities.length));
	const second = useRead(async () => setFactors((await new Factors(client).of()).factors.length));
	const latest = useRead(async () => setRecent((await client.get<{ entries: { seq: number; at: string; action: string }[] }>('/v1/account/activity', { ended: 'caller' })).entries.slice(0, 3)));

	useEffect(() => {
		for (const read of [listed, linked, second, latest]) void read.run();
		// Once, when the page opens: each `run` stays the same
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function save() {
		if (!name.trim()) return;
		setBusy(true);
		setFailure(undefined);
		try {
			await accounts.prefer({ name: name.trim() });
			await refresh();
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so the name may be saved: it is read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(false);
		}
	}

	const account = identity?.account;
	const memberships = identity?.organizations ?? [];
	// A role is not repeated on rows that all share it (L3)
	const differ = memberships.length < 2 || new Set(memberships.map(membership => membership.role)).size > 1;
	const description = (product: Product) => product.description?.[language] ?? product.description?.en;

	// The first run's steps, once what each depends on is known; all done, the checklist goes. The factor
	// step is not known while the factors are read or Accounts does not answer them, and is left out when
	// they were refused
	const refused = Boolean(second.unread && !second.unread.unreachable);
	const steps = useMemo(() => {
		if (!account || (factors === undefined && !refused)) return undefined;
		const list = [
			{ key: 'name', title: text('home.setup.name'), done: Boolean(account.name), action: { label: text('home.setup.name.action'), href: '#name' } },
			...(factors === undefined ? [] : [{ key: 'factor', title: text('home.setup.factor'), detail: text('home.setup.factor.detail'), done: factors > 0, action: { label: text('home.setup.factor.action'), href: '/security#factors' } }]),
			{ key: 'organization', title: text('home.setup.organization'), done: memberships.length > 0, action: { label: text('organizations.create'), href: '/organizations?view=create' } }
		];
		return list.every(step => step.done) ? undefined : list.map(({ done, ...step }) => ({ ...step, state: done ? ('done' as const) : undefined }));
	}, [account, factors, refused, memberships.length, text]);
	const outage = (read: { unread?: Refusal }) => Boolean(read.unread?.unreachable);

	return (
		<Screen template="overview" width="fluid" title={text('nav.overview')} facts={[account?.name, account?.email].filter(Boolean).join(' · ') || undefined}>
			<Outage
				reads={[
					{ key: 'products', title: text('outage.products'), ...listed },
					{ key: 'ways', title: text('identities.title'), ...linked },
					{ key: 'factors', title: text('factors.title'), ...second },
					{ key: 'activity', title: text('nav.activity'), ...latest }
				]}
			>
				<Deletion />
				{ignored ? <Callout tone="warning" live title={text('overview.ignored')} /> : null}
				{failure ? <Problem error={failure} /> : null}

				{account && !account.name ? (
					<Panel id="name" title={text('home.name.title')}>
						{/* Save waits quietly while the name is empty, and a press puts the caret in it (D183) */}
						<Form
							className="flex max-w-form flex-col gap-3 sm:flex-row sm:items-end"
							onSubmit={event => {
								event.preventDefault();
								void save();
							}}
						>
							<div className="flex-1">
								<Field label={text('account.name')} hint={text('account.name.help')}>
									<input value={name} maxLength={100} autoComplete="name" required onChange={event => setName(event.target.value)} />
								</Field>
							</div>
							<Button type="submit" variant="primary" busy={busy}>
								{text('action.save')}
							</Button>
						</Form>
					</Panel>
				) : null}

				<Panel
					title={text('home.security.title')}
					actions={<Go to="/security" label={text('home.security.open')} />}
					waiting={ways === undefined && !linked.unread}
					unavailable={outage(linked) && ways === undefined}
				>
					{typeof ways === 'number' ? <p className="text-body text-ink">{text('home.security.ways', { count: ways })}</p> : null}
					{/* The recent activity Accounts did not answer says so in its place, under the ways that did */}
					{outage(latest) ? <EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} /> : null}
					{recent?.length ? (
						<ul className="mt-2 flex flex-col gap-1 text-small text-ink-muted">
							{recent.map(entry => (
								<li key={entry.seq}>
									{text(`activity.${entry.action}`)} · {date(entry.at)}
								</li>
							))}
						</ul>
					) : null}
					<Link className="mt-2 inline-block text-small underline" to="/activity">
						{text('nav.activity')}
					</Link>
				</Panel>

				<Panel title={text('organizations.title')}>
					{memberships.length === 0 ? (
						<EmptyState
							kind="empty"
							title={text('organizations.none.title')}
							text={text('organizations.none.body')}
							action={{ label: text('organizations.create'), href: '/organizations?view=create' }}
							labels={EmptyState.labels[language]}
						/>
					) : (
						<ul className="bui-rows" data-framed aria-label={text('organizations.title')}>
							{memberships.map(membership => (
								<ListRow key={membership.organization} title={membership.name ?? membership.organization} href={`/organizations/${membership.organization}`} meta={differ ? text(`role.${membership.role}`) : null} />
							))}
						</ul>
					)}
				</Panel>

				<Panel title={text('products.title')} description={text('products.lead')} waiting={!listed.unread && !products} unavailable={outage(listed) && !products}>
					{listed.unread && !outage(listed) ? (
						<Retry title={text(listed.unread.key)} label={text('action.retry')} busy={text('state.retrying')} onRetry={listed.run} error={listed.unread} />
					) : !products ? null : products.length === 0 ? (
						<EmptyState kind="empty" title={text('home.products.none')} labels={EmptyState.labels[language]} />
					) : (
						<ul className="bui-rows" data-framed aria-label={text('products.title')}>
							{/* Each product starts its own sign-in hand-off when opened: the verifier belongs to its server */}
							{products.map(product => (
								<ListRow
									key={product.id}
									title={product.name}
									meta={description(product) ?? null}
									href={product.entry ?? product.origins[0]}
								/>
							))}
						</ul>
					)}
				</Panel>

				{/* Known only once their reads answer, so after what is drawn: their arrival moves nothing (D185) */}
				<PendingInvitations onRead={() => setAsked(true)} />
				{steps && asked ? <Checklist title={text('home.setup.title')} steps={steps} level={2} labels={Checklist.labels[language]} /> : null}
			</Outage>
		</Screen>
	);
}
